import React, { useState, useEffect, useRef } from "react";
import { Box, Container } from "@mui/material";
import axios from "axios";
import { useHistory } from "react-router-dom";
import Marquee from "react-fast-marquee";
import Fade from "react-reveal/Fade";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import web3 from "../../../Service/web3";
import "../../counter.css";
import { colors } from "../../../../theme/colors";
import HeroStats from "./heroStates.js";
import HeroActions from "./heroActions";
import { AutoBetGlobe } from "./three";

gsap.registerPlugin(ScrollTrigger);

const Hero = ({ item, bookies, lotteries, buytotal, draws, wins, variant }) => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winnerNotifications, setWinnerNotifications] = useState([]);

  const autoBetRef = useRef(null);
  const heroBgRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_KEY;
  const history = useHistory();

  // ==========================================
  // GSAP Animations
  // ==========================================
  useEffect(() => {
    let marqueeTween;
    if (autoBetRef.current) {
      marqueeTween = gsap.to(autoBetRef.current, {
        backgroundPosition: "200% center", duration: 3.5, ease: "none", repeat: -1, repeatDelay: 4,
      });
    }
    return () => {
      if (marqueeTween) marqueeTween.kill();
    };
  }, []);

  // Desktop-only parallax on the hero background layer
  useEffect(() => {
    const mm = window.matchMedia("(min-width: 576px)");
    if (!mm.matches || !heroBgRef.current) return;

    const st = gsap.to(heroBgRef.current, {
      yPercent: -15, scale: 1.08, ease: "none", transformOrigin: "center center",
      scrollTrigger: {
        trigger: heroBgRef.current.closest(".banner-section"),
        start: "top top", end: "bottom top", scrub: 1.2, invalidateOnRefresh: true,
      },
    });

    return () => {
      st.scrollTrigger?.kill();
      st.kill();
    };
  }, []);

  // ==========================================
  // Data Fetching & Web3 (Strictly Untouched)
  // ==========================================
  useEffect(() => {
    axios.get(`${apiUrl}/news/getAllNews`).then((response) => {
      const newsData = response.data.result;
      const featuredNewsData = newsData.filter((news) => news.featured);
      setFeaturedNews(featuredNewsData);
    });

    const fetchData = async () => {
      try {
        const account = await web3.eth.getAccounts();
        let skip = 0;
        let allNotifications = [];
        let allWinnerNotifications = [];

        const fetchNotifications = async () => {
          const response = await fetch(`${apiUrl}/notifications?limit=10&skip=${skip}`, {
            method: "GET",
            headers: { Authorization: account[0] },
          });

          if (response.ok) {
            const data = await response.json();

            const filteredNotifications = data.result.filter(
              (notification) => notification.type === "LotteryCreatedAdmin" || notification.type === "LotteryCreated"
            );

            if (filteredNotifications.length > 0) {
              allNotifications = [...allNotifications, ...filteredNotifications];
              skip += 10;
              await fetchNotifications();
            } else {
              setNotifications(allNotifications);
            }

            const filteredWinnerNotifications = data.result.filter(
              (notification) => notification.type === "LotteryResult" || notification.type === "SpinLotteryResult" || notification.type === "LotteryWinner"
            );

            if (filteredWinnerNotifications.length > 0) {
              allWinnerNotifications = [...allWinnerNotifications, ...filteredWinnerNotifications];
              skip += 10;
              await fetchNotifications();
            } else {
              setWinnerNotifications(allWinnerNotifications);
            }
          } else {
            console.error("Error fetching notifications:", response.error);
          }
        };

        await fetchNotifications();
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchData();
  }, []);

  function handleRedirect(notification) {
    switch (notification.type) {
      case "LotteryCreated":
        if (notification.lotteryName === "Missile") history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine") history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL") history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      case "LotteryCreatedAdmin":
        if (notification.lotteryName === "Missile") history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine") history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL") history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      default:
        break;
    }
  }

  // ==========================================
  // Render
  // ==========================================
  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0 }}>
      {/* Top Notification Marquee */}
      {((featuredNews !== undefined && featuredNews.length > 0) || (notifications !== undefined && notifications.length > 0)) && (
        <Box sx={{ m: 0, p: 0 }}>
          <Marquee pauseOnHover gradient className="marq">
            {featuredNews.map((news) => (
              <span key={news.id} className="feature-msg mx-4">
                <a href={`/news/${news.id}`}>{news.title}</a>
              </span>
            ))}

            {notifications.slice(0, 3).map((item) => (
              <span key={item.id} onClick={() => handleRedirect(item)} className="feature-msg mx-4" style={{ cursor: "pointer" }}>
                ID-{item.genId}: "{item.title}"
              </span>
            ))}

            {winnerNotifications.slice(0, 3).map((item) => (
              <span key={item.id} onClick={() => history.push("/lottery-result")} className="feature-msg mx-4" style={{ cursor: "pointer" }}>
                ID-{item.genId}: "{item.title}"
              </span>
            ))}
          </Marquee>
        </Box>
      )}

      {/* Hero Section */}
      <Box className="banner-section">
        <Box
          ref={heroBgRef}
          className="hero-bg-layer"
          sx={{
            position: "absolute", 
            top: 0, 
            left: 0, 
            width: "100%", 
            height: "100%",
            zIndex: 0, 
            backgroundSize: "cover", 
            backgroundPosition: "right center", 
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Currently disabled model - need to resize the model and textures and delete unused meshes */}
        {/* 3D AutoBET Model  
        <Box sx={{ position: "absolute", right: 0, top: 0, width: "50%", height: "100%", zIndex: 1 }}>
          <AutoBetGlobe />
        </Box> */}

        <Box className="banner-content-area" 
        sx={{ 
          position: "relative", 
          zIndex: 2 
        }}>
          <Container 
          maxWidth={false} 
          disableGutters 
          sx={{ 
            px: { xs: 3, md: 6 } 
          }}>
            <Fade left>
              <Box sx={{ width: "100%" }}>
                <Box sx={{ width: { xs: "100%", md: "83.333%", lg: "66.667%" } }}>
                  <Box className={`banner-content${variant === "home" ? " home-hero" : ""}`}>
                    
                    {variant === "home" && (
                      <p
                        className="home-kicker"
                        style={{ color: "#000", textTransform: "uppercase", fontSize: "14px", letterSpacing: "1px", display: "flex", alignItems: "center", padding: "0 0 0 10px", marginTop: "10px", marginBottom: "5px" }}
                      >
                        DECENTRALIZED LOTTERY & GAMING ECOSYSTEM
                      </p>
                    )}

                    <h1
                      className="title"
                      style={{ textAlign: "left", fontWeight: "700", color: "#000", fontSize: "4.5rem", letterSpacing: "-1px", lineHeight: "1.1", marginTop: "0", marginBottom: "24px" }}
                    >
                      Play. Win.{" "}
                      <span style={{ color: colors.primary.main, fontSize: "inherit" }}>
                        Trust.
                      </span>
                    </h1>

                    <p
                      style={{ color: "rgba(0, 0, 0, 0.65)", fontSize: "17px", lineHeight: "1.6", maxWidth: "750px", overflowWrap: "normal", wordBreak: "normal", hyphens: "none", marginTop: "0", marginBottom: "24px" }}
                    >
                      AutoBET is a next-generation blockchain lottery and gaming platform featuring fair, transparent and globally accessible games like Powerball, Revolver, Pokies and more — all powered by smart contracts.
                    </p>

                    <HeroStats
                      bookies={bookies}
                      lotteries={lotteries}
                      buytotal={buytotal}
                      draws={draws}
                      wins={wins}
                    />

                    {variant === "home" && (
                      <HeroActions history={history} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>
      </Box>
    </Container>
  );
};

export default Hero;